import { useState } from 'react';
import { TeamActivity } from '@/hooks/useDashboardData';
import { Users, Activity, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TeamActivityFeedProps {
  activities: TeamActivity[];
  maxVisible?: number;
  embedded?: boolean;
}

export const TeamActivityFeed = ({ activities, maxVisible = 3, embedded = false }: TeamActivityFeedProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTime = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { locale: de, addSuffix: true });
    } catch {
      return 'Kürzlich';
    }
  };

  if (activities.length === 0) {
    return (
      <div className={cn(
        "relative overflow-hidden transition-all duration-500",
        !embedded && "rounded-2xl border border-border/50 bg-card p-6 hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.12)]"
      )}>
        {!embedded && <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}
        
        <div className={cn("relative z-10", embedded && "p-6")}>
          {!embedded && (
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Team-Aktivität</h3>
                <p className="text-sm text-muted-foreground">Aktivitäten deiner Workspace-Kollegen</p>
              </div>
            </div>
          )}
          <p className={cn(
            "text-sm text-muted-foreground text-center py-6",
            !embedded && "mt-4 border-t border-border/50"
          )}>
            Noch keine Team-Aktivitäten
          </p>
        </div>
      </div>
    );
  }

  const visibleActivities = isExpanded ? activities : activities.slice(0, maxVisible);
  const hasMore = activities.length > maxVisible;

  return (
    <div className={cn(
      "relative overflow-hidden transition-all duration-500",
      !embedded && "rounded-2xl border border-border/50 bg-card hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.12)]"
    )}>
      {/* Background decorative elements */}
      {!embedded && <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}

      {/* Highlight badge */}
      {!embedded && (
        <div className="absolute top-4 right-4 z-20">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            <Sparkles className="w-3 h-3" />
            Live
          </span>
        </div>
      )}

      <div className={cn("relative z-10", !embedded && "p-6")}>
        {/* Header */}
        {!embedded && (
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Team-Aktivität</h3>
              <p className="text-sm text-muted-foreground">Aktivitäten deiner Workspace-Kollegen</p>
            </div>
          </div>
        )}

        {/* Activity List */}
        <div className={cn("space-y-3", embedded && "p-6 pt-4")}>
          {visibleActivities.map((activity, index) => (
            <div 
              key={activity.id} 
              className="group flex items-start gap-3 text-sm p-3 rounded-xl transition-all duration-300 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent border border-transparent hover:border-primary/10"
            >
              {/* Activity indicator */}
              <div className="relative">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                  <Activity className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                {/* Connection line */}
                {index < visibleActivities.length - 1 && (
                  <div className="absolute top-8 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-primary/20 to-transparent" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-foreground">
                  <span className="font-semibold">{activity.creator_name || 'Ein Teammitglied'}</span>
                  {' '}hat eine neue Entscheidung{' '}
                  {activity.decision_type && (
                    <>im Bereich <span className="font-medium text-primary">{activity.decision_type}</span></>
                  )}
                  {' '}gestartet
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatTime(activity.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Show more/less button */}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "w-full mt-4 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5",
              embedded && "mx-6 w-[calc(100%-3rem)]"
            )}
          >
            {isExpanded ? (
              <>
                Weniger anzeigen
                <ChevronUp className="h-3.5 w-3.5 ml-1" />
              </>
            ) : (
              <>
                +{activities.length - maxVisible} weitere Aktivitäten
                <ChevronDown className="h-3.5 w-3.5 ml-1" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
